import { Button, Divider, Typography } from "@shared/ui";
import { Flex } from "antd";
import React from "react";
import styles from "./EmailPreview.module.scss";

const { Title, Text, Paragraph } = Typography;

export interface EmailPreviewProps {
  influencerFirstName: string;
  campaignName: string;
  brandName: string;
  platform: string;
  username: string;
  deliverables: string;
  commercials: string;
  campaignTimeline: string;
  paymentTerms: string;
  onConfirm?: () => void;
  confirmText?: string;
  termsUrl?: string;
  className?: string;
  fromEmailId?: string;
  toCreatorEmail?: string;
  subject?: string;
  deliverablesNotesHtml?: string;
  confirmationNotes?: string;
  isCampaignTimelineFlexible?: boolean;
  travelDetails?: string;
}

export const EmailPreview: React.FC<EmailPreviewProps> = ({
  travelDetails,
  confirmationNotes,
  deliverablesNotesHtml,
  influencerFirstName,
  campaignName,
  brandName,
  platform,
  username,
  deliverables,
  commercials,
  campaignTimeline,
  paymentTerms,
  onConfirm,
  confirmText = "Confirm Campaign",
  termsUrl = "#",
  className,
  fromEmailId,
  toCreatorEmail,
  subject,
  isCampaignTimelineFlexible,
}) => {
  return (
    <div className={`${styles.emailPreviewRoot} ${className ?? ""}`}>
      {(fromEmailId || toCreatorEmail) && (
        <div className={styles.emailMetaRow}>
          {fromEmailId && (
            <div className={styles.emailMetaBlock}>
              <span className={styles.emailMetaLabel}>From Email ID</span>
              <span className={styles.emailMetaValue}>{fromEmailId}</span>
            </div>
          )}
          {toCreatorEmail && (
            <div className={styles.emailMetaBlock}>
              <span className={styles.emailMetaLabel}>To Creator ID</span>
              <span className={styles.emailMetaValue}>{toCreatorEmail}</span>
            </div>
          )}
        </div>
      )}
      {subject && (
        <div className={styles.subjectBlock}>
          <span className={styles.emailMetaLabel}>Subject</span>
          <span className={styles.emailMetaValue}>{subject}</span>
        </div>
      )}
      <span className={styles.previewLabel}>Email Preview</span>
      <div className={styles.emailPreviewScroll}>
        <div className={styles.emailCard}>
          <Title level={2} className={styles.emailCardTitle}>
            Campaign Confirmation
          </Title>

          <Paragraph className={styles.emailCardParagraph}>
            <Text strong>Hi {influencerFirstName},</Text>
          </Paragraph>

          <Paragraph>
            We’re delighted to inform you that your profile has been confirmed
            for the upcoming <Text strong>{campaignName}</Text> campaign in
            collaboration with <Text strong>{brandName}</Text>, via Cloutflow.
          </Paragraph>

          <Paragraph>
            Thank you for being a part of our curated network of creators. We’re
            excited to see the impact your participation will create for this
            campaign.
          </Paragraph>

          <Title level={5} className={styles.campaignDetailsTitle}>
            Campaign Details
          </Title>

          <div className={styles.campaignDetailsList}>
            <div>
              <Text strong>Platform</Text>: <Text>{platform}</Text>
            </div>
            <div>
              <Text strong>Username</Text>: <Text>{username}</Text>
            </div>
            <div>
              <Text strong>Brand Name</Text>: <Text>{brandName}</Text>
            </div>
            <div>
              <Text strong>Deliverables</Text>: <Text>{deliverables}</Text>
            </div>
            <div>
              <Text strong>Commercial&apos;s</Text>: <Text>{commercials}</Text>
            </div>
            <div>
              <Text strong>Campaign timeline</Text>:{" "}
              <Text>
                {campaignTimeline}{" "}
                {isCampaignTimelineFlexible ? "(Flexible)" : ""}
              </Text>
            </div>
            <div>
              <Text strong>Payment Term&apos;s</Text>:{" "}
              <Text>{paymentTerms}</Text>
            </div>
            {confirmationNotes && (
              <div>
                <Text strong>Confirmation Notes</Text>:{" "}
                <Text>{confirmationNotes}</Text>
              </div>
            )}
            {deliverablesNotesHtml && (
              <Flex gap={8} align="flex-start">
                <Text strong>Deliverable Special Notes:</Text>
                <div
                  dangerouslySetInnerHTML={{
                    __html: deliverablesNotesHtml || "",
                  }}
                />
              </Flex>
            )}
            {travelDetails && (
              <div>
                <Text strong>Travel Details</Text>: <Text>{travelDetails}</Text>
              </div>
            )}
          </div>

          <div className={styles.confirmButtonWrap}>
            <Button
              type="primary"
              size="large"
              onClick={onConfirm}
              className={styles.confirmButton}
            >
              {confirmText}
            </Button>
          </div>

          <Divider className={styles.termsDivider} />

          <Paragraph className={styles.termsParagraph}>
            By confirming your participation in this campaign, you acknowledge
            that you have read, understood, and agreed to the Terms and
            Conditions outlined in the document linked below. These Terms and
            Conditions are binding and govern your participation in the
            campaign.
          </Paragraph>
          <div className={styles.termsLinkWrap}>
            <a href={termsUrl} target="_blank" rel="noreferrer">
              Terms and conditions
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreview;
